import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Apple, ArrowDown, Download, ShieldCheck, Smartphone } from "lucide-react";
import axios from "axios";
import { PlayStoreIcon } from "./icons";
import PhoneMockup from "./PhoneMockup";
import { APK_DOWNLOAD_URL, APK_INFO_URL, SETTINGS_URL } from "@/lib/constants";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden pb-1 ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPhone = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 40]);
  const [apk, setApk] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios
      .get(APK_INFO_URL)
      .then((res) => setApk(res.data))
      .catch(() => setApk({ available: false }));
    axios
      .get(SETTINGS_URL)
      .then((res) => setSettings(res.data))
      .catch(() => setSettings({ mode: "apk", url: "" }));
  }, []);

  const apkSize = apk?.available ? `${(apk.size / 1048576).toFixed(1).replace(".", ",")} MB` : null;
  const linkMode = settings?.mode === "link" && Boolean(settings?.url);
  const downloadHref = linkMode ? settings.url : APK_DOWNLOAD_URL;
  const canDownload = linkMode || !apk || apk.available;
  const noteText = linkMode
    ? "Descarga desde el enlace oficial"
    : apkSize
      ? `Descarga directa disponible · ${apkSize}`
      : "Descarga directa disponible muy pronto";

  return (
    <section ref={ref} className="grain relative overflow-hidden bg-[#12263a] pb-24 pt-32 sm:pt-36 lg:pb-32" data-testid="hero-section">
      <div className="pointer-events-none absolute -right-32 top-10 h-96 w-96 rounded-full bg-[#14b8a6]/10 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -left-40 bottom-0 h-80 w-80 rounded-full bg-[#0f766e]/20 blur-3xl" aria-hidden="true" />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-10 left-0 select-none font-serif text-[24vw] font-bold leading-none text-transparent lg:text-[15rem]"
        style={{ WebkitTextStroke: "1px rgba(255,255,255,0.05)" }}
      >
        nidoria
      </span>

      <div className="relative mx-auto grid max-w-6xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-[1.15fr_0.85fr]">
        <motion.div style={{ y: yText }}>
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#14b8a6]/40 bg-[#14b8a6]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[#5eead4]"
            data-testid="hero-tag"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#f59e0b]" aria-hidden="true" />
            Alquiler y venta · Toda España
          </motion.span>

          <h1 className="mt-6 font-serif text-4xl font-semibold leading-[1.08] text-white sm:text-5xl lg:text-6xl" data-testid="hero-title">
            <MaskedLine delay={0.15}>Tu próximo hogar,</MaskedLine>
            <MaskedLine delay={0.3} className="italic text-[#14b8a6]">
              en tu bolsillo.
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 max-w-xl text-base text-white/70 sm:text-lg"
            data-testid="hero-subtitle"
          >
            Descarga la app de Nidoria y encuentra pisos y casas en alquiler o venta en toda España, a los mejores precios.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            {canDownload ? (
              <a
                href={downloadHref}
                {...(linkMode ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                data-testid="hero-android-button"
                className="inline-flex items-center gap-3 rounded-2xl border border-white/15 bg-black px-5 py-3 text-white transition-transform duration-200 hover:scale-[1.04] active:scale-95"
              >
                <PlayStoreIcon className="h-7 w-7" />
                <span className="text-left leading-tight">
                  <span className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-white/70">Descargar para</span>
                  <span className="block text-lg font-extrabold">Android</span>
                </span>
              </a>
            ) : (
              <span
                data-testid="hero-android-button"
                aria-disabled="true"
                title="Disponible en breve"
                className="inline-flex cursor-not-allowed select-none items-center gap-3 rounded-2xl border border-white/15 bg-black/60 px-5 py-3 text-white/50"
              >
                <PlayStoreIcon className="h-7 w-7 opacity-50" />
                <span className="text-left leading-tight">
                  <span className="block text-[10px] font-semibold uppercase tracking-[0.12em]">Disponible en breve</span>
                  <span className="block text-lg font-extrabold">Android</span>
                </span>
              </span>
            )}
            <span
              data-testid="hero-ios-button-disabled"
              aria-disabled="true"
              title="Próximamente en App Store"
              className="inline-flex cursor-not-allowed select-none items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-5 py-3 text-white/40"
            >
              <Apple className="h-7 w-7" />
              <span className="text-left leading-tight">
                <span className="block text-[10px] font-semibold uppercase tracking-[0.12em]">Próximamente en</span>
                <span className="block text-lg font-extrabold">App Store</span>
              </span>
            </span>
            <a
              href="#como-funciona"
              data-testid="hero-how-it-works-button"
              className="inline-flex items-center gap-2 rounded-2xl px-4 py-3.5 text-sm font-bold text-white/85 transition-colors duration-200 hover:text-[#14b8a6]"
            >
              Cómo funciona
              <ArrowDown className="h-4 w-4" />
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 space-y-2.5 text-sm text-white/70"
          >
            <li className="flex items-start gap-2.5" data-testid="hero-note-no-payments">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#14b8a6]" />
              No pedimos dinero ni documentación. Todo se hace en persona.
            </li>
            <li className="flex items-start gap-2.5" data-testid="hero-note-android-only">
              <Smartphone className="mt-0.5 h-4 w-4 shrink-0 text-[#14b8a6]" />
              Por ahora la app solo funciona en Android. Versión para iPhone, próximamente.
            </li>
            <li className="flex items-start gap-2.5" data-testid="hero-note-direct-download">
              <Download className="mt-0.5 h-4 w-4 shrink-0 text-[#f59e0b]" />
              {noteText}
            </li>
          </motion.ul>
        </motion.div>

        <motion.div
          style={{ y: yPhone }}
          initial={{ opacity: 0, y: 48, rotate: 2 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-[320px]"
        >
          <div className="absolute -inset-5 -rotate-2 rounded-[3.4rem] border-2 border-[#14b8a6]/25" aria-hidden="true" />
          <div className="relative rotate-2 transition-transform duration-500 hover:rotate-0">
            <PhoneMockup />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
